import * as React from 'react';
import {
  Button,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';

interface IApproveWorkChecks {
  workChecks: Array<IWorkChecks>;
}

interface IWorkChecks {
  requiredBy: string;
  prompt: string;
  description: string;
}

interface ApproveWorkDialogProps {
  onClose: () => void;
  showModal: boolean;
  barcode: string;
  setWorkChecksRequired: (value: boolean) => void;
  approveWork: () => void;
}

const ApproveWorkDialog: React.FC<ApproveWorkDialogProps> = ({
  showModal, onClose, barcode, setWorkChecksRequired, approveWork,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [workApprovedCount, setWorkApprovedCount] = React.useState(0);

  const handleClose = () => {
    setWorkApprovedCount(0);
    onClose();
  };

  const approveWorkChecksFetcher = (url: string, barcodeParam: string) => axios.get<IApproveWorkChecks>(url, {
    params: {
      barcode: barcodeParam,
    },
  })
    .then((response) => {
      const workChecksRequired = response.data.workChecks.length > 0;
      setWorkChecksRequired(workChecksRequired);
      return response.data;
    })
    .catch((error: AxiosError) => {
      if (error?.message) {
        enqueueSnackbar(error?.message, { persist: false, variant: 'error' });
      }
    });

  const { data: workChecksData } = useSWR(barcode
    ? ['/packer/getApproveWorkChecks', barcode]
    : null,
  (url, barcodeParam) => approveWorkChecksFetcher(url, barcodeParam));

  const handleWorkApproved = () => {
    const countChecked = workApprovedCount + 1;
    setWorkApprovedCount(countChecked);
    if (workChecksData?.workChecks.length === countChecked) {
      approveWork();
      onClose();
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      open={showModal}
      fullWidth
      maxWidth="md"
    >
      {workChecksData?.workChecks
        && (
          <>
            <DialogTitle>
              <Typography variant="overline">
                {`REQUIRED BY ${workChecksData.workChecks[workApprovedCount]?.requiredBy || 'M&G'}`}
              </Typography>
              <Typography variant="h5" id="dialog-header">{workChecksData.workChecks[workApprovedCount]?.prompt}</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">{workChecksData.workChecks[workApprovedCount]?.description}</Typography>
            </DialogContent>
            <DialogActions>
              <Button color="primary" variant="text" onClick={handleClose}>{'CANCEL'}</Button>
              <Button color="primary" variant="contained" onClick={() => { handleWorkApproved(); }}>{'YES, WORK APPROVED'}</Button>
            </DialogActions>
          </>
        )}
    </Dialog>
  );
};

export default ApproveWorkDialog;
